import React, { useState, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { FileText, ChevronLeft } from 'lucide-react';
import { REPORTS, eyvoLogo } from './InventoryReports/reportUtils';
import { ReportFooter } from './InventoryReports/ReportShared';
import { useAuth } from '../contexts/AuthContext';
import {
  StockValuationReport, BatchExpiryReport, SerialAuditTrailReport,
  StockMovementReport, WriteOffReport, ReplenishmentReport
} from './InventoryReports/ManagerReports';
import {
  CostOfGoodsReport, StockAgingReport, CycleCountVarianceReport,
  PeriodComparisonReport, ValuationAuditReport
} from './InventoryReports/AccountantReports';

const REPORT_COMPONENTS = {
  'stock-valuation': StockValuationReport,
  'batch-expiry': BatchExpiryReport,
  'serial-audit-trail': SerialAuditTrailReport,
  'stock-movements': StockMovementReport,
  'write-offs': WriteOffReport,
  'replenishment-performance': ReplenishmentReport,
  'cost-of-goods-issued': CostOfGoodsReport,
  'stock-aging': StockAgingReport,
  'cycle-count-variance': CycleCountVarianceReport,
  'period-comparison': PeriodComparisonReport,
  'valuation-audit': ValuationAuditReport,
};

export default function InventoryReports() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const cur = systemSettings?.system_currency || 'USD';
  const [selectedReport, setSelectedReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});

  const runReport = useCallback(async (reportId, params = {}) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== undefined))).toString();
      const r = await api.get(`/api/inventory/mgmt-reports/${reportId}${qs ? '?' + qs : ''}`);
      setData(r.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load report'));
    }
    setLoading(false);
  }, []);

  if (!selectedReport) {
    return (
      <div data-testid="inventory-reports-home">
        <div className="mb-5">
          <h2 className="text-base font-bold"><FileText className="h-4 w-4 inline mr-1.5 text-primary" /> Inventory Reports</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Comprehensive reporting for inventory managers and accountants. All reports support PDF and CSV export.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {REPORTS.map(r => (
            <button key={r.id} onClick={() => { setSelectedReport(r.id); setData(null); setFilters({}); }}
              className="flex items-start gap-3 p-4 border rounded-xl text-left hover:bg-accent/20 hover:border-primary/30 transition-all group"
              data-testid={`report-card-${r.id}`}>
              <div className={`p-2 rounded-lg ${r.group === 'Accountant' ? 'bg-emerald-50' : r.group === 'Both' ? 'bg-purple-50' : 'bg-blue-50'}`}>
                <r.icon className={`h-5 w-5 ${r.group === 'Accountant' ? 'text-emerald-600' : r.group === 'Both' ? 'text-purple-600' : 'text-blue-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold group-hover:text-primary transition-colors">{r.label}</span>
                  <Badge className={`text-[8px] ${r.group === 'Accountant' ? 'bg-emerald-100 text-emerald-700' : r.group === 'Both' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {r.group}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{r.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const report = REPORTS.find(r => r.id === selectedReport);
  const ReportComponent = REPORT_COMPONENTS[selectedReport];

  return (
    <div data-testid={`report-view-${selectedReport}`}>
      <div className="flex items-center gap-3 mb-4">
        <Button size="sm" variant="ghost" onClick={() => { setSelectedReport(null); setData(null); }} data-testid="report-back">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-base font-bold">{report && <report.icon className="h-4 w-4 inline mr-1.5 text-primary" />}{report?.label}</h2>
          <p className="text-xs text-muted-foreground">{report?.desc}</p>
        </div>
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" data-testid="eyvo-logo-link">
          <img src={eyvoLogo} alt="Eyvo" className="h-8 opacity-90 hover:opacity-100 transition-opacity" />
        </a>
      </div>

      {ReportComponent && (
        <ReportComponent
          data={data}
          loading={loading}
          runReport={runReport}
          filters={filters}
          setFilters={setFilters}
          reportId={selectedReport}
          tz={tz}
          cur={cur}
        />
      )}

      <ReportFooter />
    </div>
  );
}
